import { FormInstance } from "antd/lib/form";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import Vocabulary from "components/Vocabulary";
import WizardFormV2 from "components/WizardFormV2";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { useHasDisabledEnvironment } from "contexts/GlobalSettingsContext";
import _, { isEmpty } from "lodash";
import React, {
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getEmptyCancellableRequest } from "restAPI";
import SubProductEnvironmentTypeAPI from "restAPI/SubProductEnvironmentTypeAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";
import { envParseSelectOptionsWithValueAsName } from "views/Findings/Constants";

import { TOOL_TYPE_FIELD_OPTIONS } from "./JiraSecurityToolConstants";
import {
  CommonIdNameProps,
  JiraSecurityToolLoginConfigProps,
  JiraSecurityToolMappingToolFieldProps,
  JiraSecurityToolRecordProps,
  SelectFieldOptionProps,
} from "./JiraSecurityToolPropTypes";
import {
  generatePayload,
  getCustomSeverityFields,
  getJiraSecurityToolFormInitialData,
  getMappingToolFields,
  getStatusFieldOptions,
  getStatusMappingFields,
} from "./JiraSecurityToolUtils";

type JiraSecurityToolMappinFormProps = {
  loginConfig: JiraSecurityToolLoginConfigProps;
  onUpdateConfigComponent: (value: boolean) => void;
  record: JiraSecurityToolRecordProps;
  url: string;
  name: string;
};
const JiraSecurityToolMappinForm = ({
  loginConfig,
  onUpdateConfigComponent,
  url,
  name,
  ...props
}: JiraSecurityToolMappinFormProps) => {
  const [initialData] = useState(() =>
    getJiraSecurityToolFormInitialData(props?.record),
  );
  const isEdit = Boolean(props.record?.id);
  const [data, setData] = useState(initialData || null);
  //   allFieldsData contains all data which will be used to create payload when submitting
  const [mappingFiedsData, setMappingFieldsData] = useState<{
    toolFields?: JiraSecurityToolMappingToolFieldProps[];
    subtaskFields?: JiraSecurityToolMappingToolFieldProps[];
    priorityFields?: { label: string; value: string }[];
    statusFields?: { label: string; value: string }[];
  }>();
  // custom mapping field options
  const [fieldOptions, setFieldOptions] = useState<
    { label: string; value: string }[]
  >([]);
  // custom mapping field options filtered by date type
  const [dateFieldOptions, setDateFieldOptions] = useState<
    { label: string; value: string }[]
  >([]);
  // cusom mapping field options filtered by number type
  const [numberFieldOptions, setNumberFieldOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [environments, setEnvironments] = useState<string>();
  const [selectedProduct, setSelectedProduct] = useState<{
    name: string;
    id: number;
  } | null>(
    props.record
      ? { name: props.record.product_name!, id: props.record.product_id! }
      : null,
  );
  const [selectedSubProduct, setSelectedSubProduct] = useState<{
    name: string;
    id: number;
  } | null>(
    props.record
      ? {
          name: props.record.sub_product_name!,
          id: props.record.sub_product_id!,
        }
      : null,
  );
  const [customSeverityFieldOptions, setCustomSeverityFieldOptions] =
    useState<SelectFieldOptionProps[]>();
  const [customSeveritiesOptions, setCustomSeveritiesOptions] =
    useState<SelectFieldOptionProps[]>();
  const [form, setForm] = useState<FormInstance>();
  const thisRef: MutableRefObject<any> = useRef();
  const { hasDisabledEnvironment, envValue } = useHasDisabledEnvironment();

  // Since the option's label and value are identical, we haven't added
  // special handling for the case where a previously selected option
  // exists on a different (next) page of the dropdown.
  // Because of this, when the form is reopened, the selected value
  // should appear correctcly if it isn't part of the currently loaded page.
  const getProjects = useCallback(
    (params) => {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName("Jira", {
          login_id: loginConfig.id,
          ...params,
        });
      const updatedPromise = promise.then((res) => {
        return res.projects.map((p: CommonIdNameProps) => ({
          id: p.name,
          name: p.name,
        }));
      });
      return { promise: updatedPromise, cancel };
    },
    [loginConfig.id],
  );

  const getIssutTypes = useCallback(() => {
    if (data?.projectKey) {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName("Jira", {
          login_id: loginConfig.id,
          project: data?.projectKey,
          type: "issueType",
        });
      const updatedPromise = promise.then((res) => {
        return res.projects[0].otherProperties.issueTypes.map(
          (issueType: CommonIdNameProps) => ({
            id: issueType.name,
            name: issueType.name,
          }),
        );
      });
      return { promise: updatedPromise, cancel };
    }
    return getEmptyCancellableRequest([]);
  }, [loginConfig.id, data?.projectKey]);

  //get mapping tool fields data
  useEffect(() => {
    if (loginConfig.id && data?.projectKey && data?.issueType) {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getJiraSecurityToolMappingToolFields({
          loginId: loginConfig.id,
          projectKey: data?.projectKey,
          findingType: data?.issueType,
          ...(data?.onlySubtasks ? { onlySubtasks: true } : {}),
        });
      promise
        .then((res) => {
          const { data: responseData } = res || {};
          if (!isEmpty(responseData?.toolFields)) {
            const { fields } = responseData.toolFields.parentFields || {};
            const { subtaskFields } = responseData.toolFields || {};
            const fieldOptions = fields.map(
              (f: JiraSecurityToolMappingToolFieldProps) => ({
                label: data?.onlySubtasks ? `parent.${f.name}` : f.name,
                value: data?.onlySubtasks ? `parent.${f.name}` : f.name,
              }),
            );
            const customSeverityFieldOptions = fields
              .filter(
                (f: JiraSecurityToolMappingToolFieldProps) =>
                  !isEmpty(f.allowedValues),
              )
              .map((f: JiraSecurityToolMappingToolFieldProps) => ({
                label: data?.onlySubtasks ? "parent." + f.name : f.name,
                value: data?.onlySubtasks ? "parent." + f.name : f.name,
              }));
            if (!isEmpty(subtaskFields) && !isEmpty(subtaskFields.fields)) {
              const subTaskFieldOptions = subtaskFields.fields.map(
                (subtaskField: JiraSecurityToolMappingToolFieldProps) => {
                  return {
                    label: `subtask.${subtaskField.name}`,
                    value: `subtask.${subtaskField.name}`,
                  };
                },
              );
              fieldOptions.push(...subTaskFieldOptions);
              const findCustomSeverityFieldOptionsInSubtaskFields =
                subtaskFields.fields
                  .filter(
                    (f: JiraSecurityToolMappingToolFieldProps) =>
                      !isEmpty(f.allowedValues),
                  )
                  .map((f: JiraSecurityToolMappingToolFieldProps) => ({
                    label: `subtask.${f.name}`,
                    value: `subtask.${f.name}`,
                  }));
              customSeverityFieldOptions.push(
                ...findCustomSeverityFieldOptionsInSubtaskFields,
              );
              setMappingFieldsData((prevData) => ({
                ...prevData,
                subtaskFields: subtaskFields.fields,
              }));
            }
            const dateFieldOptions = fields
              .filter(
                (f: JiraSecurityToolMappingToolFieldProps) => f.type === "date",
              )
              .map((f: JiraSecurityToolMappingToolFieldProps) => ({
                label: f.name,
                value: f.name,
              }));
            const numberFieldOptions = fields
              .filter(
                (f: JiraSecurityToolMappingToolFieldProps) =>
                  f.type === "number",
              )
              .map((f: JiraSecurityToolMappingToolFieldProps) => ({
                label: f.name,
                value: f.name,
              }));
            setMappingFieldsData((prevData) => ({
              ...prevData,
              toolFields: fields,
            }));
            setFieldOptions(fieldOptions);
            setDateFieldOptions(dateFieldOptions);
            setNumberFieldOptions(numberFieldOptions);
            setCustomSeverityFieldOptions(customSeverityFieldOptions);
          }
          if (responseData.status?.length) {
            setMappingFieldsData((prevData) => ({
              ...prevData,
              statusFields: responseData.status.map(
                (o: { id: string; name: string }) => ({
                  label: o.name,
                  value: o.name,
                }),
              ),
            }));
          }
        })
        .catch((e) => console.log(e));

      return cancel;
    }
    return;
  }, [data?.projectKey, data?.issueType, loginConfig.id, data?.onlySubtasks]);

  // set severity fields options in edit mode
  useEffect(() => {
    if (props.record && form) {
      try {
        const severityMapping = JSON.parse(
          props.record.severityMapping as string,
        );
        const { ticketFieldName } = severityMapping || {};
        const selectedSeverityField: JiraSecurityToolMappingToolFieldProps =
          mappingFiedsData?.toolFields?.find(
            (f) =>
              f.name === ticketFieldName.replace(/^(parent|subtask)\./, ""),
          ) || {};
        const severityOptions = selectedSeverityField.allowedValues || [];
        setCustomSeveritiesOptions(
          _.map(severityOptions, (f) => ({
            label: f.name,
            value: f.name,
          })),
        );
      } catch (error) {
        console.log(error);
      }
    }
  }, [form, mappingFiedsData?.toolFields, props.record]);

  useEffect(() => {
    if (selectedSubProduct?.id) {
      if (form) {
        form.resetFields(["environment"]);
      }
      setEnvironments("");
      const { promise, cancel } = SubProductEnvironmentTypeAPI.getAllQuick(
        selectedSubProduct?.id,
      );
      promise.then((response) => {
        setEnvironments(response);
      });
      return cancel;
    }
  }, [selectedSubProduct?.id, form]);

  const onOk = useCallback(
    async (data: JiraSecurityToolRecordProps) => {
      await form!.validateFields();

      const payload = generatePayload(
        // @ts-expect-error
        mappingFiedsData,
        data,
        loginConfig,
        selectedProduct?.name,
        selectedSubProduct?.name,
      );
      if (isEdit) {
        await ToolsIntegrationsAPI.updateGenericConfigurationByToolName({
          ...payload,
          environment: hasDisabledEnvironment ? envValue : payload.environment,
        }).promise;
      } else {
        await ToolsIntegrationsAPI.postGenericToolsConfiguration([
          {
            ...payload,
            environment: hasDisabledEnvironment
              ? envValue
              : payload.environment,
          },
        ]).promise;
      }
      message.success("Configuration saved successfully");
      onUpdateConfigComponent && onUpdateConfigComponent(true);
      setData({
        product: undefined,
        subProduct: undefined,
        id: undefined,
        login_id: undefined,
        sub_product_name: undefined,
        product_name: undefined,
        projectKey: undefined,
        issueType: undefined,
        jqlFilter: undefined,
        environment: undefined,
      });
      thisRef.current.close();
    },
    [
      form,
      isEdit,
      loginConfig,
      mappingFiedsData,
      onUpdateConfigComponent,
      selectedProduct?.name,
      selectedSubProduct?.name,
      envValue,
      hasDisabledEnvironment,
    ],
  );

  const resetSubProductsField = useCallback(
    (value, _: any, selectedOption: { value: number; label: string }) => {
      setData((prevData) => ({
        ...prevData,
        product: value,
        subProduct: prevData.subProduct || undefined,
      }));
      setSelectedProduct({
        id: selectedOption.value,
        name: selectedOption.label,
      });
      form!.setFieldsValue({ subProduct: null });
    },
    [form],
  );

  const modalProps = {
    title: `${isEdit ? "Edit" : "Add"} Jira Mapping`,
    width: MODAL_SIZE_LARGE,
    footer: null,
    className: "modal-full-height no-footer",
  };

  const productId = data.product;

  const onCreateSubproduct = useCallback(
    (createdSubproduct) => {
      form!.setFieldsValue({
        subProduct: createdSubproduct.id,
      });
      setData((prevdata: JiraSecurityToolRecordProps) => ({
        ...prevdata,
        subProduct: createdSubproduct.id,
      }));
    },
    [form],
  );

  const onCustomSeverityFieldChange = useCallback(
    (value: string) => {
      if (!value) {
        form!.setFieldsValue({
          critical: null,
          high: null,
          medium: null,
          low: null,
          info: null,
        });
        setCustomSeveritiesOptions([]);
        return;
      }
      const selectedSeverityField = mappingFiedsData?.toolFields?.find(
        (f) => f.name === value.replace(/^(parent|subtask)\./, ""),
      ) as JiraSecurityToolMappingToolFieldProps; // type assertion for non-null type
      form!.setFieldsValue({
        critical: null,
        high: null,
        medium: null,
        low: null,
        info: null,
      });
      setCustomSeveritiesOptions(
        _.map(selectedSeverityField?.allowedValues, (f) => ({
          label: f.name,
          value: f.name,
        })),
      );
    },
    [mappingFiedsData?.toolFields, form],
  );

  const productSubProductFields = useMemo(
    () => [
      {
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: isEdit,
        props: {
          dropdownType: "product",
          showAddButton: true,
          onChange: resetSubProductsField,
          onCreate: (createdProduct: CommonIdNameProps) => {
            form!.setFieldsValue({
              product: createdProduct.id,
              subProduct: undefined,
            });
            setData((data: JiraSecurityToolRecordProps) => ({
              ...data,
              product: createdProduct.id,
              subProduct: undefined,
            }));
          },
        },
      },
      {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: isEdit,
        props: {
          productId: productId,
          showAddButton: Boolean(productId),
          dropdownType: "subProduct",
          onCreate: onCreateSubproduct,
          onChange: (
            value: string,
            _: any,
            selectedOption: { value: number; label: string },
          ) => {
            setSelectedSubProduct({
              id: selectedOption.value,
              name: selectedOption.label,
            });
          },
        },
      },
      ...(hasDisabledEnvironment
        ? []
        : [
            {
              label: "Environment",
              name: "environment",
              disabled: isEdit,
              colSpan: 12,
              type: FieldTypes.SELECT,
              options: envParseSelectOptionsWithValueAsName(environments),
              loading: _.isNull(environments),
              rules: [
                {
                  required: true,
                  message: <>Select an environment</>,
                },
              ],
            },
          ]),
    ],
    [
      form,
      productId,
      resetSubProductsField,
      onCreateSubproduct,
      environments,
      hasDisabledEnvironment,
      isEdit,
    ],
  );

  const fields = useMemo(
    () => [
      ...productSubProductFields,
      {
        name: "projectKey",
        label: "Project Key",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        props: {
          api: getProjects,
          onChange: (value: string) => {
            setData((prevData) => ({
              ...prevData,
              projectKey: value,
            }));
          },
        },
      },
      {
        name: "issueType",
        label: "Issue Type",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        props: {
          api: getIssutTypes,
          resetOptionsProp: getIssutTypes,
          onChange: (value: string) => {
            setData((prevData: JiraSecurityToolRecordProps) => ({
              ...prevData,
              issueType: value,
            }));
          },
        },
      },
      {
        name: "onlySubtasks",
        label: "Sub-tasks Only",
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        props: {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            setData((prevData: JiraSecurityToolRecordProps) => ({
              ...prevData,
              onlySubtasks: e.target.checked,
            }));
          },
        },
      },
      {
        name: "toolType",
        label: "Scan Type",
        colSpan: 12,
        options: TOOL_TYPE_FIELD_OPTIONS,
        type: FieldTypes.SELECT,
      },
      {
        label: "JQL Filter",
        colSpan: 24,
        name: "jqlFilter",
      },
    ],
    [getIssutTypes, getProjects, productSubProductFields],
  );

  const customMappingFields = useMemo(() => {
    const fields = [
      ...getMappingToolFields({
        fieldOptions,
        dateTypeFieldOptions: dateFieldOptions,
        numberTypeFieldOptions: numberFieldOptions,
      }),
      ...getCustomSeverityFields({
        onSeverityFieldChange: onCustomSeverityFieldChange,
        severityFieldOptions: customSeverityFieldOptions!,
        severities: customSeveritiesOptions!,
      }),
      ...getStatusMappingFields({
        getOptions: (options, selectedKey) =>
          getStatusFieldOptions(data, selectedKey, options),
        statusFieldOptions: mappingFiedsData?.statusFields ?? [],
      }),
    ];
    return fields;
  }, [
    customSeveritiesOptions,
    customSeverityFieldOptions,
    dateFieldOptions,
    fieldOptions,
    mappingFiedsData?.statusFields,
    numberFieldOptions,
    onCustomSeverityFieldChange,
    data,
  ]);

  const pages = [
    {
      name: "Map Project Key/Issue Type",
      fields,
    },
    {
      name: "Mapping Fields",
      fields: customMappingFields,
      formLayout: "horizontal",
    },
  ];

  const onCancel = useCallback(() => {
    thisRef.current.close();
  }, [thisRef]);

  const onFormChange = useCallback(
    (changedValue: Record<string, any>, allValues: Record<string, any>) => {
      setData((prevData: JiraSecurityToolRecordProps) => ({
        ...prevData,
        ...allValues,
      }));
    },
    [],
  );

  //TODO need to resolve TS
  return (
    <BaseModal
      ref={thisRef}
      modalProps={modalProps}
      // @ts-expect-error
      afterClose={props.afterClose}
    >
      <WizardFormV2
        isEdit={!_.isUndefined(props.record)}
        onCancel={onCancel}
        pages={pages}
        initialValues={initialData}
        api={onOk}
        formRef={setForm}
        onChange={onFormChange}
      />
    </BaseModal>
  );
};

export default forwardRef(JiraSecurityToolMappinForm);
